// ../shared/src/asserts.ts
function assert(b, msg = "Assertion failed") {
  if (!b) {
    throw new Error(typeof msg === "string" ? msg : msg());
  }
}
function assertString(v) {
  assertType(v, "string");
}
function assertNumber(v) {
  assertType(v, "number");
}
function assertBoolean(v) {
  assertType(v, "boolean");
}
function assertType(v, t) {
  if (typeof v !== t) {
    throwInvalidType(v, t);
  }
}
function assertObject(v) {
  if (v === null) {
    throwInvalidType(v, "object");
  }
  assertType(v, "object");
}
function assertArray(v) {
  if (!Array.isArray(v)) {
    throwInvalidType(v, "array");
  }
}
function invalidType(v, t) {
  let s = "Invalid type: ";
  if (v === null || v === void 0) {
    s += v;
  } else {
    s += `${typeof v} \`${v}\``;
  }
  return s + `, expected ${t}`;
}
function throwInvalidType(v, t) {
  throw new Error(invalidType(v, t));
}
function assertNotNull(v) {
  if (v === null) {
    throw new Error("Expected non-null value");
  }
}
function unreachable(_) {
  throw new Error("Unreachable");
}

// ../shared/src/must.ts
function must(v, msg) {
  if (v == null) {
    throw new Error(msg ?? `Unexpected ${v} value`);
  }
  return v;
}

// ../zql/src/ivm/data.ts
import { compareUTF8 } from "compare-utf8";
function compareValues(a, b) {
  a = normalizeUndefined(a);
  b = normalizeUndefined(b);
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return -1;
  }
  if (b === null) {
    return 1;
  }
  if (typeof a === "boolean") {
    assertBoolean(b);
    return a ? 1 : -1;
  }
  if (typeof a === "number") {
    assertNumber(b);
    return a - b;
  }
  if (typeof a === "string") {
    assertString(b);
    return compareUTF8(a, b);
  }
  throw new Error(`Unsupported type: ${a}`);
}
function normalizeUndefined(v) {
  return v ?? null;
}
function makeComparator(order, reverse) {
  return (a, b) => {
    for (const ord of order) {
      const field = ord[0];
      const comp = compareValues(a[field], b[field]);
      if (comp !== 0) {
        const result = ord[1] === "asc" ? comp : -comp;
        return reverse ? -result : result;
      }
    }
    return 0;
  };
}
function valuesEqual(a, b) {
  if (a == null || b == null) {
    return false;
  }
  return a === b;
}
function drainStreams(node) {
  for (const stream of Object.values(node.relationships)) {
    for (const node2 of stream()) {
      drainStreams(node2);
    }
  }
}

// ../zql/src/ivm/view-apply-change.ts
var refCountSymbol = Symbol("rc");
function applyChange(parentEntry, change, schema, relationship, format) {
  if (schema.isHidden) {
    switch (change.type) {
      case "add":
      case "remove":
        for (const [relationship2, children] of Object.entries(
          change.node.relationships
        )) {
          const childSchema = must(schema.relationships[relationship2]);
          for (const node of children()) {
            applyChange(
              parentEntry,
              { type: change.type, node },
              childSchema,
              relationship2,
              format
            );
          }
        }
        return;
      case "edit":
        return;
      case "child": {
        const childSchema = must(
          schema.relationships[change.child.relationshipName]
        );
        applyChange(
          parentEntry,
          change.child.change,
          childSchema,
          relationship,
          format
        );
        return;
      }
      default:
        unreachable(change);
    }
  }
  const { singular, relationships: childFormats } = format;
  switch (change.type) {
    case "add": {
      let newEntry;
      let rc = 1;
      if (singular) {
        const oldEntry = parentEntry[relationship];
        if (oldEntry !== void 0) {
          assert(
            schema.compareRows(oldEntry, change.node.row) === 0,
            `Singular relationship '${relationship}' should not have multiple rows. You may need to declare this relationship with the \`many\` helper instead of the \`one\` helper in your schema.`
          );
          rc = oldEntry[refCountSymbol] + 1;
        }
        newEntry = makeNewEntryWithRefCount(change.node.row, rc);
        parentEntry[relationship] = newEntry;
      } else {
        newEntry = makeNewEntryAndInsert(
          change.node.row,
          getChildEntryList(parentEntry, relationship),
          schema.compareRows
        );
      }
      for (const [relationship2, children] of Object.entries(
        change.node.relationships
      )) {
        const childSchema = must(schema.relationships[relationship2]);
        const childFormat = childFormats[relationship2];
        if (childFormat === void 0) {
          continue;
        }
        const newView = childFormat.singular ? void 0 : [];
        newEntry[relationship2] = newView;
        for (const node of children()) {
          applyChange(
            newEntry,
            { type: "add", node },
            childSchema,
            relationship2,
            childFormat
          );
        }
      }
      break;
    }
    case "remove": {
      if (singular) {
        const oldEntry = parentEntry[relationship];
        assert(oldEntry !== void 0, "node does not exist");
        const rc = oldEntry[refCountSymbol];
        if (rc === 1) {
          parentEntry[relationship] = void 0;
        }
        oldEntry[refCountSymbol]--;
      } else {
        removeAndUpdateRefCount(
          getChildEntryList(parentEntry, relationship),
          change.node.row,
          schema.compareRows
        );
      }
      drainStreams(change.node);
      break;
    }
    case "child": {
      let existing;
      if (singular) {
        existing = getSingularEntry(parentEntry, relationship);
      } else {
        const view = getChildEntryList(parentEntry, relationship);
        const { pos, found } = binarySearch(
          view,
          change.node.row,
          schema.compareRows
        );
        assert(found, "node does not exist");
        existing = view[pos];
      }
      const childSchema = must(
        schema.relationships[change.child.relationshipName]
      );
      const childFormat = format.relationships[change.child.relationshipName];
      if (childFormat !== void 0) {
        applyChange(
          existing,
          change.child.change,
          childSchema,
          change.child.relationshipName,
          childFormat
        );
      }
      break;
    }
    case "edit": {
      if (singular) {
        const existing = parentEntry[relationship];
        assertRCEntry(existing);
        const rc = existing[refCountSymbol];
        const newEntry = {
          ...existing,
          ...change.node.row,
          [refCountSymbol]: rc
        };
        existing[refCountSymbol] = 0;
        parentEntry[relationship] = newEntry;
      } else {
        const view = getChildEntryList(parentEntry, relationship);
        if (schema.compareRows(change.oldNode.row, change.node.row) === 0) {
          const { pos, found } = binarySearch(
            view,
            change.oldNode.row,
            schema.compareRows
          );
          assert(found, "node does not exist");
          const oldEntry = view[pos];
          const rc = oldEntry[refCountSymbol];
          oldEntry[refCountSymbol] = 0;
          const newEntry = makeEntryPreserveRelationships(
            change.node.row,
            oldEntry,
            format.relationships,
            rc
          );
          view[pos] = newEntry;
        } else {
          const oldEntry = removeAndUpdateRefCount(
            view,
            change.oldNode.row,
            schema.compareRows
          );
          insertAndSetRefCount(
            view,
            change.node.row,
            oldEntry,
            format.relationships,
            schema.compareRows
          );
        }
      }
      break;
    }
    default:
      unreachable(change);
  }
}
function makeNewEntryAndInsert(newRow, view, compareRows) {
  const { pos, found } = binarySearch(view, newRow, compareRows);
  let deleteCount = 0;
  let rc = 1;
  if (found) {
    deleteCount = 1;
    rc = view[pos][refCountSymbol];
    view[pos][refCountSymbol] = rc - 1;
    rc++;
  }
  const newEntry = makeNewEntryWithRefCount(newRow, rc);
  view.splice(pos, deleteCount, newEntry);
  return newEntry;
}
function insertAndSetRefCount(view, newRow, oldEntry, relationships, compareRows) {
  const { pos, found } = binarySearch(view, newRow, compareRows);
  let deleteCount = 0;
  let rc = 1;
  if (found) {
    deleteCount = 1;
    const oldEntry2 = view[pos];
    rc = oldEntry2[refCountSymbol] + 1;
    oldEntry2[refCountSymbol] = 0;
  }
  const newEntry = makeEntryPreserveRelationships(
    newRow,
    oldEntry,
    relationships,
    rc
  );
  view.splice(pos, deleteCount, newEntry);
}
function removeAndUpdateRefCount(view, row, compareRows) {
  const { pos, found } = binarySearch(view, row, compareRows);
  assert(found, "node does not exist");
  const oldEntry = view[pos];
  const rc = oldEntry[refCountSymbol];
  if (rc === 1) {
    view.splice(pos, 1);
  }
  oldEntry[refCountSymbol]--;
  return oldEntry;
}
function binarySearch(view, target, comparator) {
  let low = 0;
  let high = view.length - 1;
  while (low <= high) {
    const mid = low + high >>> 1;
    const comparison = comparator(view[mid], target);
    if (comparison < 0) {
      low = mid + 1;
    } else if (comparison > 0) {
      high = mid - 1;
    } else {
      return { pos: mid, found: true };
    }
  }
  return { pos: low, found: false };
}
function makeEntryPreserveRelationships(newRow, oldEntry, relationships, rc) {
  const entry = makeNewEntryWithRefCount(newRow, rc);
  for (const relationship in relationships) {
    assert(!(relationship in newRow), "Relationship already exists");
    entry[relationship] = oldEntry[relationship];
  }
  return entry;
}
function getChildEntryList(parentEntry, relationship) {
  const view = parentEntry[relationship];
  assertArray(view);
  return view;
}
function assertRCEntry(v) {
  assertNumber(v[refCountSymbol]);
}
function getSingularEntry(parentEntry, relationship) {
  const e = parentEntry[relationship];
  assertNumber(e[refCountSymbol]);
  return e;
}
function makeNewEntryWithRefCount(row, rc) {
  return { ...row, [refCountSymbol]: rc };
}

export {
  assert,
  assertString,
  assertNumber,
  assertBoolean,
  assertObject,
  assertArray,
  throwInvalidType,
  assertNotNull,
  unreachable,
  must,
  compareValues,
  normalizeUndefined,
  makeComparator,
  valuesEqual,
  drainStreams,
  applyChange
};
//# sourceMappingURL=chunk-2NCGIK6G.js.map
